import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { notificationEventName } from 'lib/notificationBus';

const MAX_QUEUE_SIZE = 8;
const DEDUPE_WINDOW_MS = 8000;

export default function GlobalNotification() {
    const [queue, setQueue] = useState([]);
    const [dedupeMap, setDedupeMap] = useState({});

    useEffect(() => {
        const handleNotification = (event) => {
            const detail = event?.detail || {};
            const message = String(detail.message || '').trim();
            if (!message) return;

            const type = String(detail.type || 'info');
            const dedupeKey = detail.dedupeKey ? String(detail.dedupeKey) : `${type}:${message}`;
            const now = Date.now();

            setDedupeMap((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((key) => {
                    if (now - next[key] > DEDUPE_WINDOW_MS) delete next[key];
                });

                if (next[dedupeKey] && now - next[dedupeKey] <= DEDUPE_WINDOW_MS) {
                    return next;
                }

                next[dedupeKey] = now;
                setQueue((prevQueue) => {
                    const item = {
                        id: `${now}-${Math.random().toString(16).slice(2)}`,
                        type,
                        message,
                        persist: Boolean(detail.persist),
                        duration: Number(detail.duration) > 0 ? Number(detail.duration) : 5000,
                    };

                    const nextQueue = [...prevQueue, item];
                    return nextQueue.slice(-MAX_QUEUE_SIZE);
                });
                return next;
            });
        };

        window.addEventListener(notificationEventName, handleNotification);
        return () => {
            window.removeEventListener(notificationEventName, handleNotification);
        };
    }, []);

    const current = useMemo(() => queue[0] || null, [queue]);

    const dismissCurrent = () => {
        setQueue((prev) => prev.slice(1));
    };

    return (
        <Snackbar
            open={Boolean(current)}
            autoHideDuration={current?.persist ? null : current?.duration || 5000}
            onClose={(event, reason) => {
                if (reason === 'clickaway' && current?.persist) return;
                dismissCurrent();
            }}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert
                onClose={dismissCurrent}
                severity={current?.type || 'info'}
                variant="filled"
            >
                {current?.message || ''}
            </Alert>
        </Snackbar>
    );
}
